/*****************************************
 ./src/components/Modal/Modal.tsx
 *****************************************/

import React, { ReactNode } from 'react';
import { Modal as MuiModal, Box, Fade, Backdrop, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as styles from './Modal.module.scss';
import InvidooButton, { InvidooButtonVariant } from '../InvidooButton';
 
 interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm?: () => void;
   title?: string | ReactNode;
   message?: ReactNode;
   confirmText?: string | React.ReactNode;
   cancelText?: string;
   children?: ReactNode;
   startIcon?: ReactNode;
   buttonDisabled?: boolean;
   cancelButtonDisabled?: boolean; // Separate prop for cancel button - defaults to false so cancel is always available
   hasBorderBox?: boolean;
   showConfirmButton?: boolean;
   showCancelButton?: boolean;
   showCloseButton?: boolean;
   // Optional THIRD action button (rendered between confirm and cancel). Hidden by
   // default so existing two-button modals are unaffected. Has its own handler so it
   // does NOT collide with onClose (which also fires on backdrop/X dismissal).
   onTertiary?: () => void;
   tertiaryText?: string | React.ReactNode;
   showTertiaryButton?: boolean;
   tertiaryButtonDisabled?: boolean;
   // Optional per-button style variant overrides. Defaults preserve the current look
   // (primary confirm, secondary cancel) for backward compatibility.
   confirmVariant?: 'primary' | 'secondary' | 'text-button';
   cancelVariant?: 'primary' | 'secondary' | 'text-button';
   tertiaryVariant?: 'primary' | 'secondary' | 'text-button';
   // Reverse the visual order of the action buttons. Default render order is
   // confirm → tertiary → cancel; with this true it becomes cancel → tertiary → confirm
   // (i.e. the primary/confirm button sits LAST). Mobile-first safe (column + row).
   reverseActions?: boolean;
  // Opt-in: lay the action buttons out as a centered flex-wrap (each ~50% basis) from
  // sm+ instead of full-width stacked. Phones (<sm) keep the stacked full-width layout.
  // Labels get white-space: nowrap. Off by default so existing modals are unaffected.
  wrapActions?: boolean;
    width?: string;
    minWidth?: string;
    className?: string;
    centered?: boolean;
    size?: 'auto' | 'small' | 'medium' | 'large' | 'full' | string; // 'auto' hugs content, others set max-width
    // Theme color props for guest page theming
    backgroundColor?: string;
    textColor?: string;
    // Custom content areas for special modals
    headerContent?: ReactNode; // Content above title (e.g., icons, images)
    footerContent?: ReactNode; // Content rendered BELOW the action buttons (e.g. a "don't ask again" checkbox)
    headerContentBackground?: string; // Background image URL for headerContent area only
    backgroundImage?: string; // URL for background image (entire modal)
    customStyles?: React.CSSProperties; // Inline styles for modal box
}
 
// Map legacy variant names to InvidooButton variant
const toButtonVariant = (v: 'primary' | 'secondary' | 'text-button'): InvidooButtonVariant =>
  v === 'text-button' ? 'text' : v;

const Modal: React.FC<ModalProps> = ({
   isOpen,
   onClose,
   onConfirm,
   title = "Bekræftelse",
   children,
    message,
    confirmText = "Bekræft",
    cancelText = "Fortryd",
   buttonDisabled = false,
   cancelButtonDisabled = false, // Cancel button is always enabled by default
   hasBorderBox = false,
   showConfirmButton = true,
   showCancelButton = true,
   showCloseButton = false,
   onTertiary,
   tertiaryText = "Mere",
   showTertiaryButton = false,
   tertiaryButtonDisabled = false,
   confirmVariant = 'primary',
   cancelVariant = 'secondary',
   tertiaryVariant = 'secondary',
   reverseActions = false,
   wrapActions = false,
    className = "",
    centered = false,
    size = "medium",
    backgroundColor = "white", // Default white background
    textColor = "inherit", // Default inherit text color
    headerContent,
    footerContent,
    headerContentBackground,
    backgroundImage,
    customStyles = {}
}) => {
    if (!isOpen) return null;

    // Strip transparency from background color to ensure fully opaque modal background
    const opaqueBackgroundColor = backgroundColor.includes('rgba') 
        ? backgroundColor.replace(/,\s*[\d.]+\)$/, ', 1)')
        : backgroundColor.includes('rgb') 
            ? backgroundColor.replace('rgb', 'rgba').replace(')', ', 1)')
            : backgroundColor;

    return (
         <MuiModal
             open={isOpen}
             onClose={onClose}
             aria-labelledby="modal-title"
             aria-describedby="modal-description"
             closeAfterTransition
             slots={{
                 backdrop: Backdrop
             }}
             slotProps={{
                 backdrop: {
                     timeout: 300,
                     sx: {
                         backgroundColor: 'rgba(0, 0, 0, 0.6)',
                         backdropFilter: 'blur(4px)',
                     }
                 }
             }}
         >
             <Fade in={isOpen} timeout={{ enter: 400, exit: 200 }}>
                <Box
                    className={`${styles.modalContentWrapper} ${className} ${size ? `has-size-${size}` : ''} ${centered ? `${styles.centeredModal}` : ''} ${headerContent ? styles.hasHeaderContent : ''} ${footerContent ? styles.hasFooterContent : ''}`}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: centered ? 'center' : 'left',
                        outline: 'none',
                        // Set CSS custom properties for SCSS to use
                        '--modal-bg-color': opaqueBackgroundColor,
                        '--modal-text-color': textColor,
                        // Background image if provided
                        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        // Apply custom styles (but don't override width - let CSS handle it for transitions)
                        ...customStyles,
                    }}
                >
                     {showCloseButton && (
                                                 <IconButton
                            onClick={onClose}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 1,
                                backgroundColor: 'transparent', // Remove white background
                                border: 'none', // Remove border
                                width: 32,
                                height: 32,
                                color: textColor, // Use theme text color for the icon
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.1)', // Subtle hover effect
                                },
                                '& .MuiSvgIcon-root': {
                                    fontSize: 18,
                                    fontWeight: 'bold',
                                    // Make the X more square-like in its bounding box
                                    transform: 'scale(1.1)',
                                    strokeWidth: 0.5,
                                }
                            }}
                            aria-label="close"
                        >
                             <CloseIcon />
                         </IconButton>
                     )}
                     
                     {/* Header content area (icons, images, decorations) */}
                     {headerContent && (
                         <div 
                             className={styles.modalHeaderContent}
                             style={{
                                 backgroundImage: headerContentBackground ? `url(${headerContentBackground})` : 'none',
                                 backgroundSize: 'cover',
                                 backgroundPosition: 'center',
                                 backgroundRepeat: 'no-repeat',
                             }}
                         >
                             {headerContent}
                         </div>
                     )}
                     
                     {title && !hasBorderBox && <h2 id="modal-title" className={styles.modalTitle}>{title}</h2>}
 
                     <div className={styles.modalcontent}>
                         <div id="modal-description" className={`modal-body ${styles.modalBody} ${hasBorderBox ? 'border-box-center-title small-box' : ''}`}>
                             {title && hasBorderBox && <h6 className="section-title">{title}</h6>}
                             {children || message}
                         </div>
                         {(showConfirmButton || showTertiaryButton || showCancelButton) && (
                             <div
                                 className={`${styles.modalActions}${reverseActions ? ` ${styles.actionsReverse}` : ''}${wrapActions ? ` ${styles.actionsWrap}` : ''}`}
                                 style={{ justifyContent: centered ? 'center' : 'flex-start' }}
                             >
                                {showConfirmButton && (
                                    <InvidooButton
                                        variant={toButtonVariant(confirmVariant)}
                                        size="small"
                                        className="modal-confirm-button"
                                        onClick={onConfirm}
                                        disabled={buttonDisabled}
                                    >
                                        {confirmText}
                                    </InvidooButton>
                                )}
                                {showTertiaryButton && (
                                    <InvidooButton
                                        variant={toButtonVariant(tertiaryVariant)}
                                        size="small"
                                        className="modal-tertiary-button"
                                        onClick={onTertiary}
                                        disabled={tertiaryButtonDisabled}
                                    >
                                        {tertiaryText}
                                    </InvidooButton>
                                )}
                               {showCancelButton && (
                                   <InvidooButton
                                       variant={toButtonVariant(cancelVariant)}
                                       size="small"
                                       className="modal-cancel-button"
                                       onClick={onClose}
                                       disabled={cancelButtonDisabled}
                                   >
                                       {cancelText}
                                   </InvidooButton>
                               )}
                            </div>
                        )}
                        {footerContent && (
                            <div className={styles.modalFooter}>
                                {footerContent}
                            </div>
                        )}
                    </div>
                 </Box>
             </Fade>
         </MuiModal>
     );
 };
 
 export default Modal;